// ---------------------------------------------------------------------------
// useDeepLinks — bridges Capacitor's `appUrlOpen` events into React Router so
// a shared Pleasant trip URL (e.g. https://pleasantroutes.com/?start=…&end=…)
// opens directly to the route preview when tapped on iPhone, whether it
// arrives via:
//   • Universal Links (recommended): https://pleasantroutes.com/?...
//   • Custom scheme fallback:        pleasant://?start=...&end=...
//
// Universal Links additionally require the apple-app-site-association file
// served from /.well-known/ on the production domain — see
// public/.well-known/apple-app-site-association in this project.
// ---------------------------------------------------------------------------

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp, type URLOpenListenerEvent } from "@capacitor/app";
import { handleAuthDeepLink } from "@/utils/nativeAuth";

/** Hosts we treat as "ours" when stripping a Universal Link down to a path. */
const APP_HOSTS = new Set<string>([
  "pleasantroutes.com",
  "www.pleasantroutes.com",
]);

/** Any of these schemes mean "open inside the Pleasant app". */
const APP_SCHEMES = new Set<string>(["pleasant", "pleasantroutes"]);

/**
 * Convert an inbound deep-link URL into the equivalent in-app path
 * (`/route?...` etc). Returns null when we can't recognize it — caller
 * should leave the user on whatever screen they were on.
 */
/** Siri voice navigation (pleasant://navigate?q=spoken text). Returns true
 *  when handled — the destination event goes straight to the routing brain
 *  (Index listens), skipping the router entirely. */
const handleSiriNavigate = (rawUrl: string): boolean => {
  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return false;
  }
  if (!APP_SCHEMES.has(parsed.protocol.replace(":", ""))) return false;
  const slot = (parsed.hostname || parsed.pathname.replace(/\//g, "")).toLowerCase();
  // App-icon quick action: pleasant://loop?minutes=60 (Morgan's friction
  // batch) — same event the CarPlay Loop tile fires; Index owns the rest.
  if (slot === "loop") {
    const minutes = Number(parsed.searchParams.get("minutes") ?? 60) || 60;
    window.dispatchEvent(
      new CustomEvent("pleasant:car-loop", { detail: { minutes } }),
    );
    return true;
  }
  const isNavigate = slot === "navigate";
  if (!isNavigate) return false;
  // App-icon quick action: pleasant://navigate?home=1 → route to the
  // saved Home without typing.
  if (parsed.searchParams.get("home") === "1") {
    window.dispatchEvent(new CustomEvent("pleasant:go-home"));
    return true;
  }
  const q = parsed.searchParams.get("q")?.trim();
  if (!q) return true; // navigate link with no query — swallow it
  window.dispatchEvent(
    new CustomEvent("pleasant:siri-navigate", { detail: { query: q } }),
  );
  return true;
};

export const deepLinkToInAppPath = (rawUrl: string): string | null => {
  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return null;
  }

  const isCustomScheme = APP_SCHEMES.has(parsed.protocol.replace(":", ""));
  const isOurHost = APP_HOSTS.has(parsed.hostname.toLowerCase());

  if (!isCustomScheme && !isOurHost) {
    // Some other site/app — ignore.
    return null;
  }

  // For custom schemes (pleasant://?start=…) the "path" sits in different
  // slots depending on whether the sender used pleasant://?x or
  // pleasant://route?x. Normalize both into a single search string.
  let path = parsed.pathname || "/";
  if (isCustomScheme) {
    // pleasant://?start=… parses with pathname="" and host="" — collapse
    // to "/" so React Router routes to <Index>.
    if (!path || path === "/") path = "/";
  }
  const search = parsed.search || "";
  const hash = parsed.hash || "";
  return `${path}${search}${hash}`;
};

/** Mount once near the router root. */
export const useDeepLinks = (): void => {
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!Capacitor?.isNativePlatform?.()) return;

    let removeListener: (() => void) | undefined;

    (async () => {
      // 1) Cold-start: if the OS launched us via a deep link, route now.
      try {
        const launchUrl = await CapacitorApp.getLaunchUrl();
        if (launchUrl?.url) {
          // Auth callbacks (pleasant://auth/callback#access_token=…) get
          // first crack — they don't have a meaningful in-app path; instead
          // they prime the Supabase session and close the in-app browser.
          const handledAsAuth = await handleAuthDeepLink(launchUrl.url);
          if (!handledAsAuth && !handleSiriNavigate(launchUrl.url)) {
            const path = deepLinkToInAppPath(launchUrl.url);
            if (path) navigate(path, { replace: true });
          }
        }
      } catch {
        /* ignore — getLaunchUrl is best-effort */
      }

      // 2) Warm app: subsequent taps on a Universal Link / custom scheme.
      const handle = await CapacitorApp.addListener(
        "appUrlOpen",
        async (event: URLOpenListenerEvent) => {
          const handledAsAuth = await handleAuthDeepLink(event.url);
          if (handledAsAuth) return;
          if (handleSiriNavigate(event.url)) return;
          const path = deepLinkToInAppPath(event.url);
          if (path) navigate(path);
        },
      );
      removeListener = () => {
        try { handle.remove?.(); } catch { /* ignore */ }
      };
    })();

    return () => {
      removeListener?.();
    };
  }, [navigate]);
};
